(() => {
  "use strict";

  const ROOT_ID = "ModTimeReveal";
  const DISMISS_KEY = "modtime_reveal_dismissed_at_v1";
  const COMPLETE_KEY = "modtime_reveal_completed_at_v1";
  const DAY = 86400000;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const EXCLUDED_PATHS = ["/cart", "/checkout", "/account", "/policies"];
  const TEASER_DELAY_MS = 2000;
  const PREWARM_DELAY_MS = 8000;
  const PREWARM_SCROLL_RATIO = 0.15;
  const OPEN_SCROLL_RATIO = 0.30;
  const REEL_TIMING = Object.freeze({
    tickMs: 215,
    stopMs: Object.freeze([2500, 3100, 3750]),
    settleMs: 180,
    tailCells: Object.freeze([0, 3, 6])
  });

  const root = document.getElementById(ROOT_ID);
  if (!root) return;

  const preview = root.dataset.preview === "true";
  const backdrop = root.querySelector(".mtr-backdrop");
  const dialog = root.querySelector(".mtr-dialog");
  const teaser = root.querySelector(".mtr-teaser");
  const closeButton = root.querySelector(".mtr-close");
  const declineButton = root.querySelector(".mtr-decline");
  const revealButton = root.querySelector(".mtr-reveal-button");
  const form = root.querySelector("form");
  const finishButton = root.querySelector(".mtr-finish");
  const resetButton = root.querySelector(".mtr-preview-reset");
  const status = root.querySelector(".mtr-status");
  const tracks = [...root.querySelectorAll(".mtr-track")];
  const assetNodes = [...root.querySelectorAll("[data-mtr-asset]")];
  const assets = assetNodes
    .map((node) => ({
      src: node.dataset.src,
      srcset: node.dataset.srcset || "",
      sizes: node.dataset.sizes || "480px",
      alt: node.dataset.alt || "ModTime custom watch"
    }))
    .filter((asset) => asset.src);
  const stages = [...root.querySelectorAll(".mtr-stage")];
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const assetLoadingCopy = revealButton?.querySelector("[data-asset-loading]");
  const inviteButtonCopy = revealButton?.querySelector('[data-copy="invite"]');
  let lastFocused = null;
  let isOpen = false;
  let revealing = false;
  let landed = false;
  let assetsReady = assets.length === 0;
  let assetLoadPromise = null;
  let exitReady = false;
  let inertedBranches = [];
  let pendingOpenSource = null;
  let consentRetryTimer = null;
  let reelAnimations = [];
  let spinStartedAt = null;
  let tracksBuilt = false;

  const getNumber = (key, fallback) => {
    const value = Number(root.dataset[key]);
    return Number.isFinite(value) ? value : fallback;
  };

  const storageGet = (key) => {
    try { return Number(localStorage.getItem(key) || 0); } catch (_) { return 0; }
  };
  const storageSet = (key, value) => {
    try { localStorage.setItem(key, String(value)); } catch (_) { /* storage can be unavailable */ }
  };
  const storageRemove = (key) => {
    try { localStorage.removeItem(key); } catch (_) { /* storage can be unavailable */ }
  };

  const isFresh = (key, days) => {
    const value = storageGet(key);
    return value > 0 && Date.now() - value < days * DAY;
  };

  const setStatus = (message) => { if (status) status.textContent = message; };

  const setPageInert = (enabled) => {
    if (!enabled) {
      inertedBranches.forEach(({ node, inert, ariaHidden }) => {
        node.inert = inert;
        if (ariaHidden === null) node.removeAttribute("aria-hidden");
        else node.setAttribute("aria-hidden", ariaHidden);
      });
      inertedBranches = [];
      return;
    }

    let branch = root;
    while (branch?.parentElement) {
      const parent = branch.parentElement;
      [...parent.children].forEach((node) => {
        if (node === branch || !(node instanceof HTMLElement) || ["SCRIPT", "STYLE", "LINK"].includes(node.tagName)) return;
        inertedBranches.push({ node, inert: node.inert, ariaHidden: node.getAttribute("aria-hidden") });
        node.inert = true;
        node.setAttribute("aria-hidden", "true");
      });
      if (parent === document.body) break;
      branch = parent;
    }
  };

  const getFocusable = () => [...dialog.querySelectorAll(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((node) => !node.hidden && node.offsetParent !== null);

  const showStage = (name) => {
    root.dataset.stage = name;
    stages.forEach((stage) => {
      const active = stage.dataset.stage === name;
      stage.hidden = !active;
      stage.classList.toggle("is-entering", active);
      if (active) {
        const heading = stage.querySelector(".mtr-title[id]");
        if (heading) dialog.setAttribute("aria-labelledby", heading.id);
      }
    });
  };

  const setCopy = (state) => {
    root.querySelectorAll("[data-copy]").forEach((node) => {
      node.hidden = node.dataset.copy !== state;
    });
  };

  const buildTracks = () => {
    if (tracksBuilt || !tracks.length || !assets.length) return;
    tracks.forEach((track, reelIndex) => {
      const fragment = document.createDocumentFragment();
      const offset = [0, 1, 2][reelIndex] ?? 0;
      const extraTailCells = REEL_TIMING.tailCells[reelIndex] ?? 0;
      const transientValues = (track.dataset.transientResults || "")
        .split("|")
        .map((value) => value.trim())
        .filter(Boolean);
      const insertionPoints = transientValues.map((_, index) => (
        Math.max(1, Math.round(((index + 1) * assets.length) / (transientValues.length + 1)))
      ));
      const transientIndices = [];

      const appendImageCell = (assetIndex, eager = false) => {
        const asset = assets[assetIndex % assets.length];
        const cell = document.createElement("div");
        const image = document.createElement("img");
        cell.className = "mtr-cell";
        image.alt = "";
        image.width = 960;
        image.height = 960;
        image.loading = eager ? "eager" : "lazy";
        image.fetchPriority = eager ? "high" : "low";
        image.decoding = "async";
        image.dataset.assetIndex = String(assetIndex % assets.length);
        image.sizes = asset.sizes;
        if (asset.srcset) image.srcset = asset.srcset;
        image.src = asset.src;
        cell.appendChild(image);
        fragment.appendChild(cell);
      };

      const appendOfferCell = (text, modifier) => {
        const cell = document.createElement("div");
        const label = document.createElement("span");
        cell.className = `mtr-cell mtr-cell--result ${modifier}`;
        cell.setAttribute("aria-hidden", "true");
        label.textContent = text;
        cell.appendChild(label);
        fragment.appendChild(cell);
      };

      assets.forEach((_, index) => {
        appendImageCell(index + offset, index === 0);
        insertionPoints.forEach((point, transientIndex) => {
          if (index + 1 !== point) return;
          transientIndices.push(fragment.childElementCount);
          appendOfferCell(
            transientValues[transientIndex],
            `mtr-cell--transient mtr-cell--transient-${transientIndex + 1}`
          );
        });
      });

      for (let index = 0; index < extraTailCells; index += 1) {
        appendImageCell(assets.length + offset + index);
      }

      appendOfferCell(track.dataset.result || "", "mtr-cell--final");
      track.replaceChildren(fragment);
      track.dataset.transientIndices = transientIndices.join(",");
    });
    tracksBuilt = true;
  };

  const preloadAssets = () => Promise.all(assets.map((asset) => new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ src: asset.src, ok: true });
    image.onerror = () => resolve({ src: asset.src, ok: false });
    if (asset.srcset) image.srcset = asset.srcset;
    image.sizes = asset.sizes;
    image.src = asset.src;
  })));

  const hydrateTrackImages = () => Promise.all([...root.querySelectorAll(".mtr-track img")].map((image) => {
    image.loading = "eager";
    if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
    return image.decode().then(() => true).catch(() => false);
  }));

  const ensureAssets = () => {
    if (!tracksBuilt) {
      buildTracks();
      resetReels();
    }
    if (assetsReady) return Promise.resolve([]);
    if (assetLoadPromise) return assetLoadPromise;
    if (revealButton) revealButton.disabled = true;
    if (assetLoadingCopy) assetLoadingCopy.hidden = false;
    if (inviteButtonCopy) inviteButtonCopy.hidden = true;
    setStatus("Preparing nine high-resolution ModTime watches.");
    assetLoadPromise = preloadAssets().then(async (results) => {
      await hydrateTrackImages();
      assetsReady = true;
      const failed = results.filter((result) => !result.ok);
      root.classList.toggle("has-asset-error", failed.length > 0);
      if (revealButton) revealButton.disabled = false;
      if (assetLoadingCopy) assetLoadingCopy.hidden = true;
      if (inviteButtonCopy && !revealing && !landed) inviteButtonCopy.hidden = false;
      setStatus(failed.length
        ? `${assets.length - failed.length} of ${assets.length} ModTime watches loaded.`
        : `All ${assets.length} high-resolution ModTime watches are ready.`);
      return results;
    });
    return assetLoadPromise;
  };

  const resetReels = () => {
    if (!revealButton) return;
    reelAnimations.forEach((animation) => animation.cancel());
    reelAnimations = [];
    tracks.forEach((track) => {
      track.style.transition = "none";
      track.style.transform = "translate3d(0, 0, 0)";
      delete track.dataset.stopDurationMs;
      delete track.dataset.stopElapsedMs;
    });
    root.classList.remove("is-landed");
    delete root.dataset.spinPhase;
    delete root.dataset.spinStopPlan;
    spinStartedAt = null;
    revealing = false;
    landed = false;
    setCopy("invite");
    revealButton.disabled = !assetsReady;
    showStage("reveal");
  };

  const positionReels = () => {
    tracks.forEach((track) => {
      const cell = track.querySelector(".mtr-cell");
      const count = track.children.length;
      const targetIndex = count - 1;
      const distance = cell.getBoundingClientRect().height * targetIndex;
      track.style.transition = "none";
      track.style.transform = `translate3d(0, -${distance}px, 0)`;
    });
  };

  const animateReelsContinuously = () => {
    const travelMs = REEL_TIMING.tickMs;
    const startedAt = performance.now();
    spinStartedAt = startedAt;
    const transientIndices = (tracks[0]?.dataset.transientIndices || "")
      .split(",")
      .map(Number)
      .filter(Number.isFinite);
    const transientTimes = transientIndices.map((index) => index * travelMs);
    const durations = [];
    reelAnimations.forEach((animation) => animation.cancel());
    reelAnimations = tracks.map((track, reelIndex) => {
      const cellHeight = track.querySelector(".mtr-cell").getBoundingClientRect().height;
      const finalIndex = track.children.length - 1;
      const finalDistance = cellHeight * finalIndex;
      const trackTransientIndices = (track.dataset.transientIndices || "")
        .split(",")
        .map(Number)
        .filter(Number.isFinite);
      const lastTransientIndex = trackTransientIndices.at(-1) || 0;
      const decelerationIndex = Math.min(finalIndex - 1, lastTransientIndex + 2);
      const duration = REEL_TIMING.stopMs[reelIndex] ?? finalIndex * travelMs;
      durations.push(duration);
      track.dataset.stopDurationMs = String(duration);
      track.style.transition = "none";
      const keyframes = [
        { transform: "translate3d(0, 0, 0)", offset: 0, easing: "linear" },
        ...trackTransientIndices.map((index) => ({
          transform: `translate3d(0, -${cellHeight * index}px, 0)`,
          offset: Math.min(.96, (index * travelMs) / duration),
          easing: "linear"
        }))
      ];
      if (decelerationIndex > lastTransientIndex && decelerationIndex < finalIndex) {
        keyframes.push({
          transform: `translate3d(0, -${cellHeight * decelerationIndex}px, 0)`,
          offset: Math.min(.97, (decelerationIndex * travelMs) / duration),
          easing: "cubic-bezier(.25,.25,.35,1)"
        });
      }
      keyframes.push({ transform: `translate3d(0, -${finalDistance}px, 0)`, offset: 1 });
      const animation = track.animate(keyframes, { duration, fill: "forwards" });
      animation.finished.then(() => {
        const elapsedMs = Math.round(performance.now() - startedAt);
        track.dataset.stopElapsedMs = String(elapsedMs);
        track.style.transform = `translate3d(0, -${finalDistance}px, 0)`;
        root.dispatchEvent(new CustomEvent("mtr:reel-stop", {
          detail: {
            reelIndex,
            value: track.dataset.result || "",
            plannedMs: duration,
            elapsedMs
          }
        }));
        animation.cancel();
      }).catch(() => {});
      return animation;
    });
    root.dataset.spinStopPlan = durations.join(",");
    return { transientTimes, maxDuration: Math.max(...durations), travelMs };
  };

  const reveal = () => {
    if (!revealButton) return;
    if (!assetsReady) {
      setStatus("Nine ModTime watches are still loading.");
      return;
    }
    if (revealing) return;
    if (landed) {
      showStage("form");
      window.setTimeout(() => root.querySelector("#mtr-email")?.focus(), 50);
      setStatus("10 percent is unlocked. Enter your email to receive the code.");
      return;
    }

    revealing = true;
    revealButton.disabled = true;
    setCopy("loading");
    setStatus("Your guaranteed win is being revealed.");

    const animate = !reducedMotion.matches;
    const completeReveal = () => {
      revealing = false;
      landed = true;
      root.classList.add("is-landed");
      root.dataset.spinPhase = "landed";
      revealButton.disabled = false;
      setCopy("unlocked");
      setStatus("10 percent unlocked.");
      root.dispatchEvent(new CustomEvent("mtr:reveal-complete", {
        detail: {
          stopPlanMs: REEL_TIMING.stopMs,
          settleMs: REEL_TIMING.settleMs,
          elapsedMs: spinStartedAt === null ? 0 : Math.round(performance.now() - spinStartedAt)
        }
      }));
    };

    if (animate) {
      root.dataset.spinPhase = "spinning";
      animateReelsContinuously();
      Promise.all(reelAnimations.map((animation) => animation.finished.catch(() => null)))
        .then(() => window.setTimeout(completeReveal, REEL_TIMING.settleMs));
    } else {
      root.dataset.spinPhase = "finalizing";
      spinStartedAt = performance.now();
      window.requestAnimationFrame(() => positionReels());
      window.setTimeout(completeReveal, 180);
    }
  };

  const hasVisibleShopifyConsent = () => {
    const banner = document.querySelector("#shopify-pc__banner");
    if (!banner || banner.hidden) return false;
    const style = getComputedStyle(banner);
    return style.display !== "none" && style.visibility !== "hidden" && banner.getBoundingClientRect().height > 0;
  };

  const deferUntilConsentCloses = (source) => {
    pendingOpenSource = source;
    if (consentRetryTimer) return;
    const retry = () => {
      if (hasVisibleShopifyConsent()) {
        consentRetryTimer = window.setTimeout(retry, 300);
        return;
      }
      consentRetryTimer = null;
      const deferredSource = pendingOpenSource || source;
      pendingOpenSource = null;
      open(deferredSource);
    };
    consentRetryTimer = window.setTimeout(retry, 300);
  };

  const open = (source = "automatic") => {
    if (isOpen) return;
    if (hasVisibleShopifyConsent()) {
      deferUntilConsentCloses(source);
      setStatus("Welcome offer is waiting for the privacy choice to close.");
      return;
    }
    lastFocused = document.activeElement;
    isOpen = true;
    backdrop.hidden = false;
    document.body.classList.add("mtr-scroll-locked");
    setPageInert(true);
    window.requestAnimationFrame(() => backdrop.classList.add("is-open"));
    window.setTimeout(() => {
      dialog.scrollTop = 0;
      dialog.focus({ preventScroll: true });
    }, 80);
    setStatus(`Welcome offer opened from ${source}.`);
    ensureAssets();
  };

  const close = (reason = "dismissed") => {
    if (!isOpen) return;
    isOpen = false;
    backdrop.classList.remove("is-open");
    document.body.classList.remove("mtr-scroll-locked");
    setPageInert(false);
    if (!preview && reason !== "completed") storageSet(DISMISS_KEY, Date.now());
    window.setTimeout(() => { backdrop.hidden = true; }, 240);
    const meaningfulOrigin = lastFocused
      && lastFocused !== document.body
      && lastFocused !== document.documentElement
      && typeof lastFocused.focus === "function";
    if (meaningfulOrigin) lastFocused.focus();
    else teaser.focus();
  };

  const validate = () => {
    if (!form) return false;
    const email = form.querySelector('input[type="email"]');
    const consent = form.querySelector('input[type="checkbox"]');
    const emailError = form.querySelector('[data-error="email"]');
    const consentError = form.querySelector('[data-error="consent"]');
    const validEmail = EMAIL_RE.test(email.value.trim());
    const validConsent = consent.checked;

    email.setAttribute("aria-invalid", String(!validEmail));
    emailError.hidden = validEmail;
    consentError.hidden = validConsent;
    if (!validEmail) email.focus();
    else if (!validConsent) consent.focus();
    return validEmail && validConsent;
  };

  const showSuccess = () => {
    showStage("success");
    storageSet(COMPLETE_KEY, Date.now());
    storageRemove(DISMISS_KEY);
    teaser.hidden = true;
    setStatus("Success. Your private discount code is on its way.");
    window.setTimeout(() => finishButton.focus(), 60);
  };

  const submitPreview = (event) => {
    if (!preview || !form) return;
    event.preventDefault();
    if (!validate()) return;
    const submit = form.querySelector(".mtr-submit");
    submit.disabled = true;
    form.querySelector('[data-submit="idle"]').hidden = true;
    form.querySelector('[data-submit="loading"]').hidden = false;
    setStatus("Preparing the preview success state. No data is being sent.");
    window.setTimeout(() => {
      submit.disabled = false;
      form.querySelector('[data-submit="idle"]').hidden = false;
      form.querySelector('[data-submit="loading"]').hidden = true;
      showSuccess();
    }, 850);
  };

  const onKeydown = (event) => {
    if (!isOpen) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close("escape");
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onResize = () => {
    if (landed) positionReels("final");
  };

  const installProductionTriggers = () => {
    if (EXCLUDED_PATHS.some((path) => location.pathname.startsWith(path))) return;
    if (isFresh(COMPLETE_KEY, getNumber("completeDays", 365))) return;
    if (isFresh(DISMISS_KEY, getNumber("dismissDays", 30))) return;

    const desktop = window.matchMedia("(min-width: 641px)").matches;
    const delay = desktop ? getNumber("desktopDelay", 15000) : getNumber("mobileDelay", 15000);
    let timeReady = false;
    let scrollReady = false;
    let automaticOpenRequested = false;

    teaser.hidden = true;
    window.setTimeout(() => { teaser.hidden = false; }, TEASER_DELAY_MS);

    const prepare = () => ensureAssets();
    const requestAutomaticOpen = (source) => {
      if (automaticOpenRequested || isOpen) return;
      automaticOpenRequested = true;
      prepare().finally(() => open(source));
    };
    const maybeOpen = (source) => {
      if (timeReady && scrollReady) requestAutomaticOpen(source);
    };

    window.setTimeout(() => {
      timeReady = true;
      prepare();
      maybeOpen("time and scroll engagement");
    }, delay);

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? window.scrollY / max : 0;
      if (ratio >= PREWARM_SCROLL_RATIO) prepare();
      if (ratio >= OPEN_SCROLL_RATIO) {
        scrollReady = true;
        maybeOpen("time and scroll engagement");
      }
      if (automaticOpenRequested) window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    window.setTimeout(() => {
      const idlePrepare = () => {
        if (document.visibilityState === "visible") prepare();
      };
      if ("requestIdleCallback" in window) window.requestIdleCallback(idlePrepare, { timeout: 2000 });
      else window.setTimeout(idlePrepare, 0);
    }, PREWARM_DELAY_MS);

    if (desktop) {
      window.setTimeout(() => { exitReady = true; }, 10000);
      document.addEventListener("mouseout", (event) => {
        if (exitReady && event.clientY <= 0 && !event.relatedTarget) requestAutomaticOpen("exit intent");
      });
    }
  };

  teaser.addEventListener("click", () => open("teaser"));
  teaser.addEventListener("pointerenter", ensureAssets, { once: true });
  teaser.addEventListener("focus", ensureAssets, { once: true });
  closeButton.addEventListener("click", () => close("close button"));
  declineButton.addEventListener("click", () => close("decline"));
  if (revealButton) revealButton.addEventListener("click", reveal);
  if (finishButton) finishButton.addEventListener("click", () => close("completed"));
  backdrop.addEventListener("click", (event) => { if (event.target === backdrop) close("backdrop"); });
  if (form) {
    form.addEventListener("submit", submitPreview);
    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput) {
      emailInput.addEventListener("blur", (event) => {
        if (!event.target.value) return;
        const valid = EMAIL_RE.test(event.target.value.trim());
        event.target.setAttribute("aria-invalid", String(!valid));
        const emailError = form.querySelector('[data-error="email"]');
        if (emailError) emailError.hidden = valid;
      });
    }
  }
  document.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onResize, { passive: true });

  if (resetButton) {
    resetButton.addEventListener("click", () => {
      form.reset();
      root.querySelectorAll(".mtr-error").forEach((node) => { node.hidden = true; });
      resetReels();
      teaser.hidden = false;
      if (!isOpen) open("preview reset");
      else dialog.focus();
    });
  }

  const serverSuccess = Boolean(root.querySelector('.mtr-stage--success')) && !revealButton;
  if (serverSuccess) {
    storageSet(COMPLETE_KEY, Date.now());
    storageRemove(DISMISS_KEY);
    teaser.hidden = true;
    window.setTimeout(() => open("completed signup"), 120);
  } else if (preview) {
    buildTracks();
    resetReels();
  } else {
    setCopy("invite");
    showStage("reveal");
    if (revealButton) revealButton.disabled = true;
  }
  if (preview) {
    teaser.hidden = false;
    const manualPreview = new URLSearchParams(location.search).has("manual");
    if (!manualPreview) window.setTimeout(() => open("preview"), 1200);
  } else {
    installProductionTriggers();
  }
})();
